import { lazy, Suspense, useMemo, useState } from 'react';
import { FileText, Cloud, Mail, ScrollText, LogOut, Sun, Moon, Monitor, FileEdit, Shield, BarChart3 } from 'lucide-react';
import LogoSynotis from './assets/Logo-synotis-RVB.svg';
import { useAuth } from './hooks/useAuth.js';
import { useTheme } from './hooks/useTheme.js';
import LoginView from './views/LoginView.jsx';
import GenerationView from './views/GenerationView.jsx';
import DriveView from './views/DriveView.jsx';
import MailView from './views/MailView.jsx';
import HistoryView from './views/HistoryView.jsx';
import TemplatesView from './views/TemplatesView.jsx';

// Vues lourdes (Recharts, gros tableaux) : lazy-loaded pour réduire
// la taille du bundle initial.
const DashboardView = lazy(() => import('./views/DashboardView.jsx'));
const AuditView = lazy(() => import('./views/AuditView.jsx'));

const VIEWS = [
  { key: 'generation', label: 'Génération', Icon: FileText, Component: GenerationView, adminOnly: false },
  { key: 'drive', label: 'Google Drive', Icon: Cloud, Component: DriveView, adminOnly: false },
  { key: 'mail', label: 'Envoi mail', Icon: Mail, Component: MailView, adminOnly: false },
  { key: 'dashboard', label: 'Dashboard', Icon: BarChart3, Component: DashboardView, adminOnly: false },
  { key: 'history', label: 'Historique', Icon: ScrollText, Component: HistoryView, adminOnly: false },
  { key: 'templates', label: 'Templates', Icon: FileEdit, Component: TemplatesView, adminOnly: false },
  { key: 'audit', label: 'Audit', Icon: Shield, Component: AuditView, adminOnly: true },
];

const THEME_ICONS = { auto: Monitor, light: Sun, dark: Moon };
const THEME_LABELS = { auto: 'Thème auto', light: 'Thème clair', dark: 'Thème sombre' };

export default function App() {
  const { user, loading, authError, login, logout, isAuthenticated } = useAuth();
  const { theme, cycleTheme } = useTheme();
  const [activeView, setActiveView] = useState('generation');
  const [viewParams, setViewParams] = useState({});

  // Tous les hooks doivent être appelés AVANT les early returns
  // (Rules of Hooks : ordre stable entre renders).
  const visibleViews = useMemo(
    () => VIEWS.filter((v) => !v.adminOnly || user?.isAdmin),
    [user?.isAdmin],
  );
  const ActiveComponent = useMemo(
    () => visibleViews.find((v) => v.key === activeView)?.Component ?? GenerationView,
    [activeView, visibleViews],
  );
  const ThemeIcon = THEME_ICONS[theme];

  // Navigation inter-vues avec params (ex: GenerationView → DriveView{jobId})
  const navigate = (key, params = {}) => {
    setActiveView(key);
    setViewParams(params);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
        }}
      >
        Chargement…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginView onLogin={login} authError={authError} />;
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1.5rem',
          background: 'var(--nav-bg)',
          borderBottom: '1px solid var(--glass-border)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img src={LogoSynotis} alt="Synotis" style={{ height: 32 }} />
          <strong style={{ color: 'var(--primary)' }}>CRA Generator</strong>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            onClick={cycleTheme}
            title={THEME_LABELS[theme]}
            aria-label={THEME_LABELS[theme]}
            style={iconButtonStyle}
          >
            <ThemeIcon size={18} />
          </button>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            {user?.email}
          </span>
          <button
            onClick={logout}
            title="Se déconnecter"
            aria-label="Se déconnecter"
            style={iconButtonStyle}
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <nav className="hg-nav-pills">
        {visibleViews.map(({ key, label, Icon }) => {
          const active = activeView === key;
          return (
            <button
              key={key}
              onClick={() => navigate(key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 1rem',
                borderRadius: 'var(--radius-pill)',
                border: '1px solid var(--glass-border)',
                background: active ? 'var(--primary)' : 'var(--glass)',
                color: active ? '#fff' : 'var(--text)',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: active ? 600 : 400,
              }}
            >
              <Icon size={16} />
              {label}
            </button>
          );
        })}
      </nav>

      <main style={{ flex: 1, padding: '2rem', maxWidth: 1200, width: '100%', margin: '0 auto' }}>
        <Suspense fallback={<p style={{ color: 'var(--text-muted)' }}>Chargement…</p>}>
          <ActiveComponent user={user} navigate={navigate} params={viewParams} />
        </Suspense>
      </main>
    </div>
  );
}

const iconButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.4rem',
  background: 'transparent',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  color: 'var(--text-muted)',
};
